/**
 * Custom hook for buyer admin operations
 * Handles adding users as admins to buyers with proper error handling and notifications
 */

import { useCallback, useState } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useBuyerAddUserMutationMutation } from '../../../generated';

export interface UseBuyerAdminOperationsConfig {
  onSuccess?: (adminUserIds: string[]) => void;
  onError?: (error: Error) => void;
}

export interface UseBuyerAdminOperationsResponse {
  addAdmins: (buyerId: string, userIds: string[]) => Promise<void>;
  isLoading: boolean;
}

export const useBuyerAdminOperations = ({
  onSuccess,
  onError,
}: UseBuyerAdminOperationsConfig = {}): UseBuyerAdminOperationsResponse => {
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useNotification();
  const [addUserToBuyer] = useBuyerAddUserMutationMutation();

  const addAdmins = useCallback(
    async (buyerId: string, userIds: string[]): Promise<void> => {
      if (userIds.length === 0) {
        throw new Error('No users selected');
      }

      setIsLoading(true);
      try {
        // Add each selected user as admin using the BuyerAddUserMutation
        await Promise.all(
          userIds.map((userId) =>
            addUserToBuyer({
              variables: {
                buyerId,
                userId,
                buyerUserInput: {
                  role: 'buyerAdmin', // Set as admin role
                },
              },
            })
          )
        );

        // Success notification
        addNotification({
          title: 'Admin Roles Set',
          message: `Successfully set ${userIds.length} user(s) as admin(s).`,
          type: 'success',
        });

        // Call success callback
        onSuccess?.(userIds);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An unexpected error occurred while setting admin roles.';

        // Error notification
        addNotification({
          title: 'Failed to Set Admin Roles',
          message: errorMessage,
          type: 'error',
        });

        // Call error callback
        const error = err instanceof Error ? err : new Error(errorMessage);
        onError?.(error);

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [addUserToBuyer, addNotification, onSuccess, onError]
  );

  return {
    addAdmins,
    isLoading,
  };
};
